import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Save, X, Check, Sparkles, ChevronDown, ChevronUp } from 'lucide-react';
import axios from 'axios';

const DEFAULT_ALLOWANCES = [
  { name: 'HRA', type: 'allowance', calculation_type: 'percentage', default_value: 40, applicable_on: 'basic_salary', description: 'House Rent Allowance', is_statutory: false },
  { name: 'DA', type: 'allowance', calculation_type: 'percentage', default_value: 10, applicable_on: 'basic_salary', description: 'Dearness Allowance', is_statutory: false },
  { name: 'TA', type: 'allowance', calculation_type: 'fixed', default_value: 2000, applicable_on: 'basic_salary', description: 'Transport Allowance', is_statutory: false },
  { name: 'Medical', type: 'allowance', calculation_type: 'fixed', default_value: 1500, applicable_on: 'basic_salary', description: 'Medical Allowance', is_statutory: false },
  { name: 'Special', type: 'allowance', calculation_type: 'percentage', default_value: 10, applicable_on: 'basic_salary', description: 'Special Allowance', is_statutory: false },
];

const DEFAULT_DEDUCTIONS = [
  { name: 'PF', type: 'deduction', calculation_type: 'percentage', default_value: 12, applicable_on: 'basic_salary', description: 'Provident Fund', is_statutory: true },
  { name: 'ESI', type: 'deduction', calculation_type: 'percentage', default_value: 0.75, applicable_on: 'gross_salary', description: 'Employee State Insurance', is_statutory: true },
  { name: 'PT', type: 'deduction', calculation_type: 'fixed', default_value: 200, applicable_on: 'gross_salary', description: 'Professional Tax', is_statutory: true },
  { name: 'TDS', type: 'deduction', calculation_type: 'percentage', default_value: 0, applicable_on: 'gross_salary', description: 'Tax Deducted at Source', is_statutory: true },
];

const SalaryComponents = () => {
  const [components, setComponents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingComponent, setEditingComponent] = useState(null);
  const [showQuickSetup, setShowQuickSetup] = useState(true);
  const [applyingPresets, setApplyingPresets] = useState(false);
  
  // Editable presets state
  const [presetAllowances, setPresetAllowances] = useState(
    DEFAULT_ALLOWANCES.map(function(p) { return { ...p, selected: ['HRA', 'DA', 'TA'].includes(p.name) }; })
  );
  const [presetDeductions, setPresetDeductions] = useState(
    DEFAULT_DEDUCTIONS.map(function(p) { return { ...p, selected: ['PF', 'ESI'].includes(p.name) }; })
  );
  
  const [formData, setFormData] = useState({
    name: '',
    type: 'allowance',
    calculation_type: 'percentage',
    default_value: '',
    applicable_on: 'basic_salary',
    description: '',
    is_statutory: false
  });

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem('token');

  useEffect(function() {
    fetchComponents();
  }, []);

  const fetchComponents = async function() {
    try {
      const response = await axios.get(API_URL + '/api/salary/components', {
        headers: { Authorization: 'Bearer ' + token }
      });
      setComponents(response.data);
      if (response.data.length > 0) {
        setShowQuickSetup(false);
      }
    } catch (error) {
      console.error('Error fetching components:', error);
    } finally {
      setLoading(false);
    }
  };

  const togglePresetSelection = function(type, name) {
    if (type === 'allowance') {
      setPresetAllowances(presetAllowances.map(function(p) {
        if (p.name === name) {
          return { ...p, selected: !p.selected };
        }
        return p;
      }));
    } else {
      setPresetDeductions(presetDeductions.map(function(p) {
        if (p.name === name) {
          return { ...p, selected: !p.selected };
        }
        return p;
      }));
    }
  };

  const updatePresetValue = function(type, name, newValue) {
    const value = parseFloat(newValue) || 0;
    if (type === 'allowance') {
      setPresetAllowances(presetAllowances.map(function(p) {
        if (p.name === name) {
          return { ...p, default_value: value };
        }
        return p;
      }));
    } else {
      setPresetDeductions(presetDeductions.map(function(p) {
        if (p.name === name) {
          return { ...p, default_value: value };
        }
        return p;
      }));
    }
  };

  const getSelectedCount = function() {
    const allowanceCount = presetAllowances.filter(function(p) { return p.selected; }).length;
    const deductionCount = presetDeductions.filter(function(p) { return p.selected; }).length;
    return allowanceCount + deductionCount;
  };

  const applyPresets = async function() {
    setApplyingPresets(true);
    try {
      const selectedAllowances = presetAllowances.filter(function(p) { return p.selected; });
      const selectedDeductions = presetDeductions.filter(function(p) { return p.selected; });
      const allSelected = selectedAllowances.concat(selectedDeductions);
      
      const existingNames = components.map(function(c) { return c.name.toLowerCase(); });
      const toCreate = allSelected.filter(function(p) { return !existingNames.includes(p.name.toLowerCase()); });
      
      if (toCreate.length === 0) {
        alert('All selected components already exist!');
        setApplyingPresets(false);
        return;
      }

      for (var i = 0; i < toCreate.length; i++) {
        var preset = toCreate[i];
        await axios.post(
          API_URL + '/api/salary/components',
          {
            name: preset.name,
            type: preset.type,
            calculation_type: preset.calculation_type,
            default_value: preset.default_value,
            applicable_on: preset.applicable_on,
            description: preset.description,
            is_statutory: preset.is_statutory
          },
          { headers: { Authorization: 'Bearer ' + token } }
        );
      }

      alert(toCreate.length + ' components added successfully!');
      setShowQuickSetup(false);
      fetchComponents();
    } catch (error) {
      console.error('Error applying presets:', error);
      alert('Failed to apply presets. Please try again.');
    } finally {
      setApplyingPresets(false);
    }
  };

  const handleChange = function(e) {
    var name = e.target.name;
    var value = e.target.value;
    var type = e.target.type;
    var checked = e.target.checked;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleSubmit = async function(e) {
    e.preventDefault();
    try {
      if (editingComponent) {
        await axios.put(
          API_URL + '/api/salary/components/' + editingComponent.id,
          formData,
          { headers: { Authorization: 'Bearer ' + token } }
        );
        alert('Component updated successfully!');
      } else {
        await axios.post(
          API_URL + '/api/salary/components',
          formData,
          { headers: { Authorization: 'Bearer ' + token } }
        );
        alert('Component created successfully!');
      }
      resetForm();
      fetchComponents();
    } catch (error) {
      console.error('Error saving component:', error);
      alert(error.response?.data?.detail || 'Failed to save component');
    }
  };

  const handleEdit = function(component) {
    setEditingComponent(component);
    setFormData({
      name: component.name,
      type: component.type,
      calculation_type: component.calculation_type,
      default_value: component.default_value,
      applicable_on: component.applicable_on,
      description: component.description || '',
      is_statutory: component.is_statutory || false
    });
    setShowModal(true);
  };

  const handleDelete = async function(componentId, componentName) {
    if (!window.confirm('Delete "' + componentName + '"? This cannot be undone.')) return;
    try {
      await axios.delete(API_URL + '/api/salary/components/' + componentId, {
        headers: { Authorization: 'Bearer ' + token }
      });
      alert('Component deleted!');
      fetchComponents();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to delete');
    }
  };

  const resetForm = function() {
    setFormData({
      name: '',
      type: 'allowance',
      calculation_type: 'percentage',
      default_value: '',
      applicable_on: 'basic_salary',
      description: '',
      is_statutory: false
    });
    setEditingComponent(null);
    setShowModal(false);
  };

  var allowances = components.filter(function(c) { return c.type === 'allowance'; });
  var deductions = components.filter(function(c) { return c.type === 'deduction'; });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Salary Components</h1>
          <p className="text-sm text-gray-500 mt-1">Manage allowances and deductions for staff salary</p>
        </div>
        <button
          onClick={function() { setShowModal(true); }}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus size={18} />
          Add Custom
        </button>
      </div>

      {/* Quick Setup Section */}
      {(showQuickSetup || components.length === 0) && (
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200 overflow-hidden">
          <button
            onClick={function() { setShowQuickSetup(!showQuickSetup); }}
            className="w-full px-6 py-4 flex items-center justify-between hover:bg-blue-100/50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Sparkles className="text-blue-600" size={20} />
              </div>
              <div className="text-left">
                <h3 className="font-semibold text-blue-900">Quick Setup - Standard Indian Salary Structure</h3>
                <p className="text-sm text-blue-600">Select, customize values, and apply in one click</p>
              </div>
            </div>
            {showQuickSetup ? <ChevronUp className="text-blue-600" /> : <ChevronDown className="text-blue-600" />}
          </button>
          
          {showQuickSetup && (
            <div className="px-6 pb-6 space-y-5">
              {/* Allowances */}
              <div>
                <h4 className="text-sm font-semibold text-green-700 mb-3 flex items-center gap-2">
                  <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                  Allowances (Add to Salary)
                </h4>
                <div className="space-y-2">
                  {presetAllowances.map(function(preset) {
                    return (
                      <div
                        key={preset.name}
                        className={'p-3 rounded-lg border-2 transition-all ' + (preset.selected ? 'border-green-500 bg-green-50' : 'border-gray-200 bg-white')}
                      >
                        <div className="flex items-center gap-3">
                          <input
                            type="checkbox"
                            checked={preset.selected}
                            onChange={function() { togglePresetSelection('allowance', preset.name); }}
                            className="w-5 h-5 text-green-600 rounded"
                          />
                          <div className="flex-1 flex items-center gap-3 flex-wrap">
                            <span className="font-medium text-gray-900 w-20">{preset.name}</span>
                            <div className="flex items-center gap-2">
                              {preset.calculation_type === 'percentage' ? (
                                <>
                                  <input
                                    type="number"
                                    step="0.5"
                                    min="0"
                                    max="100"
                                    value={preset.default_value}
                                    onChange={function(e) { updatePresetValue('allowance', preset.name, e.target.value); }}
                                    disabled={!preset.selected}
                                    className="w-20 px-2 py-1 border rounded text-center font-medium disabled:bg-gray-100 disabled:text-gray-400"
                                  />
                                  <span className="text-sm text-gray-600">% of Basic</span>
                                </>
                              ) : (
                                <>
                                  <span className="text-gray-500">₹</span>
                                  <input
                                    type="number"
                                    step="100"
                                    min="0"
                                    value={preset.default_value}
                                    onChange={function(e) { updatePresetValue('allowance', preset.name, e.target.value); }}
                                    disabled={!preset.selected}
                                    className="w-24 px-2 py-1 border rounded text-center font-medium disabled:bg-gray-100 disabled:text-gray-400"
                                  />
                                  <span className="text-sm text-gray-600">Fixed</span>
                                </>
                              )}
                            </div>
                            <span className="text-xs text-gray-500 ml-auto">{preset.description}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Deductions */}
              <div>
                <h4 className="text-sm font-semibold text-red-700 mb-3 flex items-center gap-2">
                  <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                  Deductions (Subtract from Salary)
                </h4>
                <div className="space-y-2">
                  {presetDeductions.map(function(preset) {
                    return (
                      <div
                        key={preset.name}
                        className={'p-3 rounded-lg border-2 transition-all ' + (preset.selected ? 'border-red-500 bg-red-50' : 'border-gray-200 bg-white')}
                      >
                        <div className="flex items-center gap-3">
                          <input
                            type="checkbox"
                            checked={preset.selected}
                            onChange={function() { togglePresetSelection('deduction', preset.name); }}
                            className="w-5 h-5 text-red-600 rounded"
                          />
                          <div className="flex-1 flex items-center gap-3 flex-wrap">
                            <span className="font-medium text-gray-900 w-20">{preset.name}</span>
                            <div className="flex items-center gap-2">
                              {preset.calculation_type === 'percentage' ? (
                                <>
                                  <input
                                    type="number"
                                    step="0.25"
                                    min="0"
                                    max="100"
                                    value={preset.default_value}
                                    onChange={function(e) { updatePresetValue('deduction', preset.name, e.target.value); }}
                                    disabled={!preset.selected}
                                    className="w-20 px-2 py-1 border rounded text-center font-medium disabled:bg-gray-100 disabled:text-gray-400"
                                  />
                                  <span className="text-sm text-gray-600">% of {preset.applicable_on === 'gross_salary' ? 'Gross' : 'Basic'}</span>
                                </>
                              ) : (
                                <>
                                  <span className="text-gray-500">₹</span>
                                  <input
                                    type="number"
                                    step="50"
                                    min="0"
                                    value={preset.default_value}
                                    onChange={function(e) { updatePresetValue('deduction', preset.name, e.target.value); }}
                                    disabled={!preset.selected}
                                    className="w-24 px-2 py-1 border rounded text-center font-medium disabled:bg-gray-100 disabled:text-gray-400"
                                  />
                                  <span className="text-sm text-gray-600">Fixed</span>
                                </>
                              )}
                            </div>
                            {preset.is_statutory && (
                              <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded text-xs font-medium">Statutory</span>
                            )}
                            <span className="text-xs text-gray-500 ml-auto">{preset.description}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Apply Button */}
              <button
                onClick={applyPresets}
                disabled={applyingPresets || getSelectedCount() === 0}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              >
                {applyingPresets ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Applying...
                  </>
                ) : (
                  <>
                    <Sparkles size={18} />
                    Apply {getSelectedCount()} Selected Components
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Current Components */}
      {components.length > 0 && (
        <>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b bg-green-50 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-green-800">Allowances</h2>
                <p className="text-sm text-green-600">Added to gross salary</p>
              </div>
              <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                {allowances.length} components
              </span>
            </div>
            <div className="p-4">
              {allowances.length === 0 ? (
                <p className="text-center text-gray-500 py-4">No allowances configured</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {allowances.map(function(comp) {
                    return (
                      <div key={comp.id} className="p-4 border rounded-lg hover:shadow-sm transition-shadow">
                        <div className="flex items-start justify-between">
                          <div>
                            <h3 className="font-semibold text-gray-900">{comp.name}</h3>
                            <p className="text-sm text-gray-500 mt-1">
                              {comp.calculation_type === 'percentage' ? comp.default_value + '% of ' + comp.applicable_on.replace('_', ' ') : '₹' + comp.default_value + ' Fixed'}
                            </p>
                          </div>
                          <div className="flex gap-1">
                            <button onClick={function() { handleEdit(comp); }} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded">
                              <Edit size={16} />
                            </button>
                            <button onClick={function() { handleDelete(comp.id, comp.name); }} className="p-1.5 text-red-600 hover:bg-red-50 rounded">
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b bg-red-50 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-red-800">Deductions</h2>
                <p className="text-sm text-red-600">Subtracted from gross salary</p>
              </div>
              <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm font-medium">
                {deductions.length} components
              </span>
            </div>
            <div className="p-4">
              {deductions.length === 0 ? (
                <p className="text-center text-gray-500 py-4">No deductions configured</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {deductions.map(function(comp) {
                    return (
                      <div key={comp.id} className="p-4 border rounded-lg hover:shadow-sm transition-shadow">
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="font-semibold text-gray-900">{comp.name}</h3>
                              {comp.is_statutory && (
                                <span className="px-1.5 py-0.5 bg-yellow-100 text-yellow-700 rounded text-xs">Statutory</span>
                              )}
                            </div>
                            <p className="text-sm text-gray-500 mt-1">
                              {comp.calculation_type === 'percentage' ? comp.default_value + '% of ' + comp.applicable_on.replace('_', ' ') : '₹' + comp.default_value + ' Fixed'}
                            </p>
                          </div>
                          <div className="flex gap-1">
                            <button onClick={function() { handleEdit(comp); }} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded">
                              <Edit size={16} />
                            </button>
                            <button onClick={function() { handleDelete(comp.id, comp.name); }} className="p-1.5 text-red-600 hover:bg-red-50 rounded">
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Empty State */}
      {components.length === 0 && !showQuickSetup && (
        <div className="text-center py-12 bg-white rounded-xl border">
          <p className="text-gray-500">No salary components configured yet.</p>
          <button onClick={function() { setShowQuickSetup(true); }} className="mt-4 text-blue-600 hover:underline">
            Use Quick Setup
          </button>
        </div>
      )}

      {/* Custom Component Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">
                {editingComponent ? 'Edit Component' : 'Add Custom Component'}
              </h3>
              <button onClick={resetForm} className="text-gray-400 hover:text-gray-600">
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Component Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., Bonus, Loan Recovery"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                  <select
                    name="type"
                    value={formData.type}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="allowance">Allowance (+)</option>
                    <option value="deduction">Deduction (-)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Calculation</label>
                  <select
                    name="calculation_type"
                    value={formData.calculation_type}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="percentage">Percentage (%)</option>
                    <option value="fixed">Fixed Amount (₹)</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Default Value <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    name="default_value"
                    value={formData.default_value}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder={formData.calculation_type === 'percentage' ? '10' : '5000'}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Based On</label>
                  <select
                    name="applicable_on"
                    value={formData.applicable_on}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="basic_salary">Basic Salary</option>
                    <option value="gross_salary">Gross Salary</option>
                  </select>
                </div>
              </div>

              {formData.type === 'deduction' && (
                <div className="flex items-center gap-2 p-3 bg-yellow-50 rounded-lg">
                  <input
                    type="checkbox"
                    name="is_statutory"
                    checked={formData.is_statutory}
                    onChange={handleChange}
                    className="w-4 h-4 text-blue-600"
                  />
                  <label className="text-sm text-gray-700">
                    Statutory deduction (PF, ESI, Tax etc.)
                  </label>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <input
                  type="text"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="Brief description (optional)"
                />
              </div>

              <div className="flex gap-3 justify-end pt-4 border-t">
                <button type="button" onClick={resetForm} className="px-4 py-2 border rounded-lg hover:bg-gray-50">
                  Cancel
                </button>
                <button type="submit" className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                  <Save size={18} />
                  {editingComponent ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SalaryComponents;
